/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { ChartSeries, TimeseriesData, ChartDataPoint, MetricStats } from 'Models';

// Define proper types for API responses
interface PrometheusResponse {
  data: {
    resultType?: string;
    result: TimeseriesData[];
  };
}

/**
 * Parse Prometheus-compatible timeseries response
 */
export const parseTimeseriesResponse = (response: PrometheusResponse): ChartSeries[] => {
  if (!response || !response.data || !response.data.result) {
    return [];
  }

  return response.data.result.map((series: TimeseriesData) => {
    const dataPoints: ChartDataPoint[] = series.values.map(([timestamp, value]) => ({
      timestamp: timestamp * 1000, // Convert to milliseconds
      value: parseFloat(String(value)),
      formattedTime: new Date(timestamp * 1000).toLocaleString()
    }));

    const stats = calculateMetricStats(dataPoints);
    const seriesName = formatSeriesName(series.metric);

    return {
      name: seriesName,
      data: dataPoints,
      stats
    };
  });
};

/**
 * Calculate statistics for a metric series
 */
export const calculateMetricStats = (dataPoints: ChartDataPoint[]): MetricStats => {
  if (dataPoints.length === 0) {
    return {
      min: NaN,
      max: NaN,
      avg: NaN,
      sum: NaN,
      count: 0,
      firstValue: NaN,
      lastValue: NaN
    };
  }

  // Filter out null/undefined values and check if we have any valid values
  const validValues = dataPoints
    .map(dp => dp.value)
    .filter(val => val !== null && val !== undefined && !isNaN(val));

  if (validValues.length === 0) {
    return {
      min: NaN,
      max: NaN,
      avg: NaN,
      sum: NaN,
      count: 0,
      firstValue: NaN,
      lastValue: NaN
    };
  }

  const sum = validValues.reduce((acc, val) => acc + val, 0);
  const avg = sum / validValues.length;
  const min = Math.min(...validValues);
  const max = Math.max(...validValues);

  // Find first and last valid values
  const firstValidIndex = dataPoints.findIndex(dp => dp.value !== null && dp.value !== undefined && !isNaN(dp.value));
  const lastValidIndex = dataPoints.length - 1 - dataPoints.slice().reverse().findIndex(dp => dp.value !== null && dp.value !== undefined && !isNaN(dp.value));

  const firstValue = firstValidIndex >= 0 ? dataPoints[firstValidIndex].value : NaN;
  const lastValue = lastValidIndex >= 0 ? dataPoints[lastValidIndex].value : NaN;

  return {
    min,
    max,
    avg,
    sum,
    count: validValues.length,
    firstValue,
    lastValue
  };
};

/**
 * Format series name from metric labels
 */
export const formatSeriesName = (metric: Record<string, string>): string => {
  if (!metric || Object.keys(metric).length === 0) {
    return 'Unknown Metric';
  }

  // Try to find a meaningful name from common label patterns
  const name = metric.__name__ || metric.name || metric.metric || metric.job || metric.instance;

  if (name) {
    // Add additional context if available, but exclude the main metric name
    const additionalLabels = Object.entries(metric)
      .filter(([key]) => !['__name__', 'name', 'metric', 'job', 'instance'].includes(key))
      .map(([key, value]) => `${key}="${value}"`)
      .join(', ');

    // Return just the name without the {} wrapper
    return name;
  }

  // Fallback to just the first label without {}
  const firstLabel = Object.entries(metric)[0];
  return firstLabel ? firstLabel[0] : 'Unknown Metric';
};

/**
 * Check if response is in Prometheus format
 */
export const isPrometheusFormat = (response: PrometheusResponse): boolean => {
  return response &&
         response.data &&
         Array.isArray(response.data.result);
};

/**
 * Get time range from data points
 */
export const getTimeRange = (dataPoints: ChartDataPoint[]): { start: number; end: number } => {
  if (dataPoints.length === 0) {
    return { start: 0, end: 0 };
  }

  const timestamps = dataPoints.map(dp => dp.timestamp);
  return {
    start: Math.min(...timestamps),
    end: Math.max(...timestamps)
  };
};
